import React, { memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Button,
  Spinner,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Breadcrumbs from 'components/Common/Breadcrumb';
import { USER_PATHS } from 'routes/routesConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import {
  useStaffChangePasswordMutation,
  useStaffDetailQuery,
} from 'http-client/hooks/useStaffHooks';
import { ChangeStaffPasswordDto } from 'http-client/types/staff.types';

interface PasswordFormValues {
  newPassword: string;
  confirmNewPassword: string;
}

const ChangeStaffPasswordForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const staffId = id ?? '';

  const { data: staff, isLoading } = useStaffDetailQuery(staffId);
  const { mutate: changePassword, isPending } = useStaffChangePasswordMutation();

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .trim()
      .required('New password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmNewPassword: Yup.string()
      .trim()
      .required('Please confirm the new password')
      .oneOf([Yup.ref('newPassword')], 'Passwords must match'),
  });

  const formik = useFormik<PasswordFormValues>({
    initialValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<PasswordFormValues>) => {
      if (!staffId) return;

      const payload: ChangeStaffPasswordDto = {
        newPassword: values.newPassword.trim(),
        confirmNewPassword: values.confirmNewPassword.trim(),
      };

      changePassword(
        { id: staffId, body: payload },
        {
          onSuccess: () => navigate(USER_PATHS.BASE),
          onError: (error: unknown) => handleFormErrors<PasswordFormValues>(error, formikHelpers),
        },
      );
    },
  });

  if (isLoading) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" />
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="py-5 text-center text-muted">
        <p>Employee not found.</p>
        <Button color="secondary" onClick={() => navigate(USER_PATHS.BASE)}>
          Back to list
        </Button>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs title="Employees" breadcrumbItem="Change Password" />
      <Row className="justify-content-center">
        <Col md={12} lg={8}>
          <Card className="shadow-sm mb-4">
            <CardHeader>
              <h5 className="mb-0">Employee Details</h5>
            </CardHeader>
            <CardBody>
              <Row>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Name</p>
                  <p className="fw-semibold mb-3">{staff.name}</p>
                </Col>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Email</p>
                  <p className="fw-semibold mb-3">{staff.email}</p>
                </Col>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Phone</p>
                  <p className="fw-semibold mb-3">{staff.phoneNumber}</p>
                </Col>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Role</p>
                  <p className="fw-semibold mb-3">{staff.role?.name ?? '-'}</p>
                </Col>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Assigned Shop</p>
                  <p className="fw-semibold mb-3">{staff.assignedShop?.name ?? '-'}</p>
                </Col>
                <Col sm={6} md={4}>
                  <p className="text-muted mb-1 small">Status</p>
                  <p className="mb-3">
                    <span className={`badge ${staff.isActive ? 'bg-success' : 'bg-danger'}`}>
                      {staff.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </p>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="shadow-sm">
            <CardHeader>
              <h5 className="mb-0">Set New Password</h5>
            </CardHeader>
            <CardBody>
              <form onSubmit={formik.handleSubmit} noValidate>
                <Row>
                  <Col sm={12} md={6}>
                    <FormGroup>
                      <Label for="newPassword">New Password</Label>
                      <input
                        id="newPassword"
                        name="newPassword"
                        type="password"
                        className={`form-control ${
                          formik.touched.newPassword && formik.errors.newPassword
                            ? 'is-invalid'
                            : ''
                        }`}
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                      {formik.touched.newPassword && formik.errors.newPassword && (
                        <div className="invalid-feedback">{formik.errors.newPassword}</div>
                      )}
                    </FormGroup>
                  </Col>
                  <Col sm={12} md={6}>
                    <FormGroup>
                      <Label for="confirmNewPassword">Confirm New Password</Label>
                      <input
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        type="password"
                        className={`form-control ${
                          formik.touched.confirmNewPassword && formik.errors.confirmNewPassword
                            ? 'is-invalid'
                            : ''
                        }`}
                        value={formik.values.confirmNewPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                      {formik.touched.confirmNewPassword && formik.errors.confirmNewPassword && (
                        <div className="invalid-feedback">
                          {formik.errors.confirmNewPassword}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-center gap-2 mt-4">
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => navigate(USER_PATHS.BASE)}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button color="primary" type="submit" disabled={isPending}>
                    {isPending ? 'Updating...' : 'Update Password'}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default memo(ChangeStaffPasswordForm);
